import {
  Filter,
  Filters,
} from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { ParsedQuery } from 'query-string'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { DashboardDateRangeDefaults } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/GrcDashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import { daysToMillis } from 'src/Utils/globalHelpers'
import {
  GRC_DASHBOARD_TYPE_IS_AUDIT_SPECIFIC,
  RISK_ASSESSMENT_COLORS,
  RISK_ASSESSMENT_DATA_KEYS,
  RISK_MANAGEMENT_COLORS,
  RISK_MANAGEMENT_DATA_KEYS,
  RiskResponseSectionName,
} from './GrcDashboards.constants'
import { DashboardType } from './GrcDashboards.types'

export const getControlCheckResultLabel = (value: string): string => {
  switch (value) {
    case '0':
      return 'Failed'
    case '1':
      return 'Passed'
    case '2':
      return 'Unchecked'
    default:
      return 'Unknown'
  }
}

const getRiskManagementLabel = (value: string): string => {
  switch (value) {
    case '0':
      return 'Risks with failing mitigating controls'
    case '1':
      return 'Risks mitigated by controls'
    case '2':
      return 'Risks without associated controls'
    default:
      return 'Unknown'
  }
}

export const getIsDashboardAuditSpecific = (dname: string): boolean => {
  return GRC_DASHBOARD_TYPE_IS_AUDIT_SPECIFIC[dname as DashboardType] || false
}

export const getRiskLabelFunc = (
  section: RiskResponseSectionName,
): ((value: string) => string) => {
  if (section === 'Risk management') {
    return getRiskManagementLabel
  }
  return (value: string) => value
}

export const riskLabelFuncBoth = (value: string): string => {
  return value.split('-')[0]
}

export const applyDashboardFilters = (
  filters: Filters,
  queryParams: ParsedQuery<string>,
  filterType: FilterParam,
): void => {
  const filterParams = queryParams[filterType] as string[]

  if (!filterParams?.length) {
    return
  }

  switch (filterType) {
    case FilterParam.DASHBOARD_DEPARTMENT: {
      filters.addFilters(
        new Filter().setKey('department').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_FRAMEWORK: {
      filters.addFilters(
        new Filter().setKey('frameworks').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_POLICY: {
      filters.addFilters(
        new Filter().setKey('policies').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_AUDIT: {
      filters.addFilters(
        new Filter().setKey('audits').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_RISK: {
      filters.addFilters(
        new Filter().setKey('risks').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_ASSIGNEE: {
      filters.addFilters(
        new Filter().setKey('assignee').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_CONTROL: {
      filters.addFilters(
        new Filter().setKey('controls').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_RISK_CATEGORY: {
      filters.addFilters(
        new Filter().setKey('category').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_VENDOR: {
      filters.addFilters(
        new Filter().setKey('vendors').setValuesList(filterParams),
      )
      break
    }
    case FilterParam.DASHBOARD_DATE_RANGE: {
      switch (filterParams[0]) {
        case DashboardDateRangeDefaults.LAST_7_DAYS:
          filters.setStarttime(Date.now() - daysToMillis(7))
          break
        case DashboardDateRangeDefaults.LAST_30_DAYS:
          filters.setStarttime(Date.now() - daysToMillis(30))
          break
        case DashboardDateRangeDefaults.LAST_90_DAYS:
          filters.setStarttime(Date.now() - daysToMillis(90))
          break
        case DashboardDateRangeDefaults.LAST_YEAR:
          filters.setStarttime(Date.now() - daysToMillis(365))
          break
        default: {
          const dates = filterParams[0].split('-')
          const startMillis = parseInt(dates[0], 10)
          const endMillis = parseInt(dates[1], 10)
          filters.setStarttime(startMillis)
          filters.setEndtime(endMillis)
          break
        }
      }
      break
    }
    default:
      break
  }
}

export const getRiskColorConfig = (
  section: RiskResponseSectionName,
): Record<string, string> => {
  switch (section) {
    case RiskResponseSectionName.RISK_MANAGEMENT:
      return RISK_MANAGEMENT_COLORS
    case RiskResponseSectionName.RISK_ASSESSMENT:
      return RISK_ASSESSMENT_COLORS
  }
  return {}
}

export const getRiskDataKeys = (section: RiskResponseSectionName): string[] => {
  switch (section) {
    case RiskResponseSectionName.RISK_MANAGEMENT:
      return RISK_MANAGEMENT_DATA_KEYS
    case RiskResponseSectionName.RISK_ASSESSMENT:
      return RISK_ASSESSMENT_DATA_KEYS
  }
  return []
}
